import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useOAuthProviders } from '../hooks/useOAuthProviders';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Mail, Lock, Chrome } from 'lucide-react';
import { useShop } from '../shop/ShopContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function LoginPage() {
  const shop = useShop();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(null);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resending, setResending] = useState(false);
  const { providers: enabledProviders } = useOAuthProviders();
  const { signIn, signInWithGoogle } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const handleResendVerification = async () => {
    if (!email) {
      toast.error('Inserisci prima la tua email');
      return;
    }
    setResending(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(`${backendUrl}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await resp.json();
      if (resp.ok) {
        toast.success(data.message || 'Email di conferma inviata!');
      } else {
        toast.error(data.detail || 'Impossibile inviare l\'email di conferma');
      }
    } catch (err) {
      toast.error('Errore di rete. Riprova.');
    } finally {
      setResending(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setNeedsVerification(false);

    try {
      const { data, error } = await signIn(email, password);
      
      console.log('Login result - data:', data);
      console.log('Login result - error:', error);
      
      if (error) {
        // Handle specific Supabase auth errors with user-friendly messages
        const errorMessage = error.message?.toLowerCase() || '';
        console.log('Error message:', errorMessage);
        
        if (errorMessage.includes('invalid login credentials') || 
            errorMessage.includes('invalid_credentials') ||
            errorMessage.includes('invalid email or password')) {
          toast.error('Email o password non corretti');
        } else if (errorMessage.includes('email not confirmed') || error?.code === 'email_not_confirmed') {
          setNeedsVerification(true);
          toast.error('Verifica la tua email prima di accedere. Controlla la tua casella di posta.');
        } else if (errorMessage.includes('too many requests')) {
          toast.error('Troppi tentativi. Riprova tra qualche minuto');
        } else if (errorMessage.includes('user not found')) {
          toast.error('Nessun account trovato con questa email');
        } else {
          // Show the actual error for debugging
          toast.error(error.message || 'Errore durante l\'accesso. Verifica le credenziali');
        }
        return;
      }
      
      if (!data?.user) {
        toast.error('Errore durante l\'accesso. Riprova');
        return;
      }
      
      toast.success('Accesso effettuato con successo!');
      // Dal negozio personale si torna dove si era (es. il carrello)
      if (shop) navigate(window.history.length > 1 ? -1 : shop.paths.home);
      else navigate('/');
    } catch (error) {
      console.error('Login error:', error);
      // Generic error for unexpected issues
      toast.error(error.message || 'Errore durante l\'accesso. Verifica le credenziali e riprova');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    // Check if Google is configured in our system
    if (enabledProviders.includes('google')) {
      // Use custom OAuth flow
      setOauthLoading('google');
      try {
        const redirectUri = `${window.location.origin}/auth/callback/google`;
        const response = await fetch(`${API_URL}/api/auth/oauth/google/url?redirect_uri=${encodeURIComponent(redirectUri)}`);
        
        if (response.ok) {
          const data = await response.json();
          // Store state for CSRF verification
          sessionStorage.setItem('oauth_state', data.state);
          // Redirect to Google
          window.location.href = data.auth_url;
        } else {
          toast.error('Errore configurazione Google OAuth');
        }
      } catch (error) {
        console.error('Google OAuth error:', error);
        toast.error('Errore durante l\'accesso con Google');
      } finally {
        setOauthLoading(null);
      }
    } else {
      // Fallback to Supabase OAuth
      try {
        const { error } = await signInWithGoogle();
        if (error) {
          const errorMessage = error.message?.toLowerCase() || '';
          if (errorMessage.includes('popup')) {
            toast.error('Popup bloccato. Abilita i popup per accedere con Google');
          } else {
            toast.error('Errore durante l\'accesso con Google');
          }
        }
      } catch (error) {
        console.error('Google signin error:', error);
        toast.error('Errore durante l\'accesso con Google');
      }
    }
  };

  const handleFacebookSignIn = async () => {
    setOauthLoading('facebook');
    try {
      const redirectUri = `${window.location.origin}/auth/callback/facebook`;
      const response = await fetch(`${API_URL}/api/auth/oauth/facebook/url?redirect_uri=${encodeURIComponent(redirectUri)}`);
      
      if (response.ok) {
        const data = await response.json();
        sessionStorage.setItem('oauth_state', data.state);
        window.location.href = data.auth_url;
      } else {
        toast.error('Errore configurazione Facebook OAuth');
      }
    } catch (error) {
      console.error('Facebook OAuth error:', error);
      toast.error('Errore durante l\'accesso con Facebook');
    } finally {
      setOauthLoading(null);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 py-12 px-4" data-testid="login-page">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-sm border border-slate-200 shadow-lg p-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-3 mb-4">
              <img 
                src="/rarifex-logo.png" 
                alt="Rarifex Logo"
                className="h-16 w-auto object-contain"
              />
            </div>
            <h1 className="text-3xl font-bold text-shop-primary mb-2" style={{fontFamily: 'Inter, sans-serif'}}>
              {t('login')}
            </h1>
            <p className="text-slate-600">Bentornato su Rarifex</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="mb-2 block">{t('email')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="nome@esempio.com"
                  className="pl-10"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="mb-2 block">{t('password')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-10"
                  required
                  data-testid="password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-shop-primary text-white hover:bg-shop-primary/90 rounded-sm px-8 py-3 font-medium transition-all duration-300 shadow-lg hover:shadow-xl"
              data-testid="login-submit-btn"
            >
              {loading ? 'Accesso in corso...' : t('login')}
            </Button>

            {needsVerification && (
              <div
                className="p-3 mt-2 rounded-lg bg-amber-50 border border-amber-300 text-sm text-amber-900"
                data-testid="needs-verification-block"
              >
                <p className="mb-2">
                  Non hai ancora confermato la tua email. Controlla la casella di posta (anche lo spam) oppure richiedi un nuovo link.
                </p>
                <button
                  type="button"
                  onClick={handleResendVerification}
                  disabled={resending}
                  className="text-teal-700 font-semibold underline hover:text-teal-800 disabled:opacity-50"
                  data-testid="resend-verification-btn"
                >
                  {resending ? 'Invio in corso...' : 'Rispedisci email di conferma'}
                </button>
              </div>
            )}
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-white px-4 text-slate-500">oppure</span>
            </div>
          </div>

          <div className="space-y-3">
            {/* Google Sign In - Always show (use Supabase fallback or custom) */}
            <Button
              type="button"
              variant="outline"
              onClick={handleGoogleSignIn}
              disabled={oauthLoading === 'google'}
              className="w-full border-slate-300 hover:bg-slate-50"
              data-testid="google-signin-btn"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {oauthLoading === 'google' ? 'Connessione...' : t('sign_in_with_google')}
            </Button>

            {/* Facebook Sign In - Only show if enabled */}
            {enabledProviders.includes('facebook') && (
              <Button
                type="button"
                variant="outline"
                onClick={handleFacebookSignIn}
                disabled={oauthLoading === 'facebook'}
                className="w-full border-slate-300 hover:bg-slate-50"
                data-testid="facebook-signin-btn"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="#1877F2">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                {oauthLoading === 'facebook' ? 'Connessione...' : 'Accedi con Facebook'}
              </Button>
            )}
          </div>

          <p className="text-center mt-6 text-sm text-slate-600">
            Non hai un account?{' '}
            <Link to="/signup" className="text-shop-primary font-medium hover:underline" data-testid="signup-link">
              Registrati
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
